import React, { createContext, useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import useAuthUser from "../components/common/useAuthUser";

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const { authUser, isLoading } = useAuthUser();
  const [cart, setCart] = useState([]);
  const [hydrated, setHydrated] = useState(false);

  const storageKey = authUser ? `cart_${authUser._id}` : "cart_guest";

  /* ---------- HYDRATE CART ONCE ---------- */
  useEffect(() => {
    if (isLoading || hydrated) return;

    const stored = localStorage.getItem(storageKey);
    setCart(stored ? JSON.parse(stored) : []);
    setHydrated(true);
  }, [storageKey, isLoading, hydrated]);

  /* ---------- PERSIST CART ---------- */
  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(storageKey, JSON.stringify(cart));
  }, [cart, storageKey, hydrated]);

  /* ---------- MERGE GUEST CART ON LOGIN ---------- */
  useEffect(() => {
    if (!authUser) return;

    const guest = JSON.parse(localStorage.getItem("cart_guest") || "[]");
    if (!guest.length) return;

    setCart((prev) => {
      const merged = [...prev];

      guest.forEach((g) => {
        const found = merged.find((i) => i._id === g._id);

        if (found) {
          found.cartQuantity += g.cartQuantity;
        } else {
          merged.push(g);
        }
      });

      return merged;
    });

    localStorage.removeItem("cart_guest");
  }, [authUser]);

  /* ---------- ACTIONS ---------- */
  const addToCart = (item, qtyToAdd = 1) => {
    const safeQty = Math.max(1, Number(qtyToAdd) || 1);

    setCart((prev) => {
      const found = prev.find((p) => p._id === item._id);

      if (found) {
        return prev.map((p) =>
          p._id === item._id
            ? { ...p, cartQuantity: p.cartQuantity + safeQty }
            : p
        );
      }

      return [...prev, { ...item, cartQuantity: safeQty }];
    });

    toast.success("Added to cart");
  };

  const updateQuantity = (_id, qty) =>
    setCart((prev) =>
      prev.map((i) => (i._id === _id ? { ...i, cartQuantity: qty } : i))
    );

  const removeFromCart = (_id) =>
    setCart((prev) => prev.filter((i) => i._id !== _id));

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem(storageKey);
  };

  return (
    <CartContext.Provider
      value={{ cart, addToCart, updateQuantity, removeFromCart, clearCart }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);